// 投資スタイル診断（v2.5）の設問定義。
// 表示文言(displayLabel)はここ（フロント辞書）に持ち、判定・並べ替え・件数絞りはサーバー(Rails)が行う。
// enum値(optionId)は api_schema 生成型が正。順序はデザイン準拠。
import {
  InvestmentStyleDiagnosisRequestPurposesEnum as PurposeEnum,
  InvestmentStyleDiagnosisRequestInterest1stEnum as InterestEnum,
  InvestmentStyleDiagnosisRequestSuccessConditionsEnum as SuccessEnum,
  InvestmentStyleDiagnosisRequestMonthlyOutOfPocketEnum as MonthlyEnum,
  InvestmentStyleDiagnosisRequestHoldingPeriodEnum as HoldingEnum,
  InvestmentStyleDiagnosisRequestManagementStyleEnum as ManagementEnum,
  InvestmentStyleDiagnosisRequestShortTermSaleIntentEnum as SaleIntentEnum,
  InvestmentStyleDiagnosisRequestMeetingCheckItemsEnum as MeetingItemEnum,
  InvestmentStyleDiagnosisRequestExplanationStylesEnum as ExplanationEnum,
  InvestmentStyleDiagnosisRequestAvoidSalesStylesEnum as AvoidEnum,
  InvestmentStyleDiagnosisRequestAnxietyLoanEnum as AnxietyLevelEnum,
  InvestmentStyleDiagnosisRequestBorrowingStanceEnum as BorrowingStanceEnum,
  InvestmentStyleDiagnosisRequestRentalIncomeUseEnum as RentalIncomeUseEnum,
  InvestmentStyleDiagnosisRequestWorkOutlookEnum as WorkOutlookEnum,
  InvestmentStyleDiagnosisRequestFirstPurchaseTimingEnum as FirstPurchaseTimingEnum,
  InvestmentStyleDiagnosisRequestPlannerComparisonCountEnum as PlannerComparisonCountEnum,
} from "@/api/generated";
import type { AnxietyKey } from "@/types/investmentStyleDiagnosis";

export type Option<T extends string> = { value: T; label: string };

// 設問の識別番号は既存の1〜19を維持し、廃止したQ2を欠番にする。
// 回答一覧の表示番号は18問の連番にする。API の値・カラムとは独立している。

// Q1 実現したい未来（複数選択可）
export const PURPOSE_OPTIONS: Option<PurposeEnum>[] = [
  { value: PurposeEnum.RetirementIncome, label: "老後のお金に困らないようにしたい" },
  { value: PurposeEnum.TaxSaving, label: "できる範囲で税金の負担を軽くしたい" },
  { value: PurposeEnum.InflationHedge, label: "預金だけに頼らず、資産を守りたい" },
  { value: PurposeEnum.SideIncome, label: "毎月、給料以外の収入をつくりたい" },
  { value: PurposeEnum.LifeInsurance, label: "自分にもしものことがあっても、家族に資産を残したい" },
  { value: PurposeEnum.ShortTermGain, label: "将来的に売却して、まとまった利益を得たい" },
];

// Q3 不動産投資で大切にしたいこと（順位付きで3つ）
export const INTEREST_OPTIONS: Option<InterestEnum>[] = [
  { value: InterestEnum.StableIncome, label: "毎月、安定した収入が入ること" },
  { value: InterestEnum.LongTermAsset, label: "長い時間をかけて、着実に資産を増やすこと" },
  { value: InterestEnum.LowEffort, label: "できるだけ手間をかけずに続けられること" },
  { value: InterestEnum.HighYield, label: "多少のリスクがあっても、高い収益を目指せること" },
  { value: InterestEnum.ShortTermGain, label: "将来売却し、まとまった利益を得られること" },
  { value: InterestEnum.TaxSaving, label: "資産形成をしながら、税金の負担も抑えられること" },
  { value: InterestEnum.LossAvoidanceSafety, label: "大きな利益より、損をしにくいこと" },
  { value: InterestEnum.LossAvoidanceLowInitial, label: "最初に用意するお金をできるだけ抑えること" },
  { value: InterestEnum.LossAvoidanceTrusted, label: "信頼できる人に相談しながら進められること" },
];

// Q4 あなたにとっての成功（複数選択可）
export const SUCCESS_OPTIONS: Option<SuccessEnum>[] = [
  { value: SuccessEnum.RetirementMonthly, label: "老後の生活費を、家賃収入で補えている" },
  { value: SuccessEnum.AvoidBigLoss, label: "大きな損をせず、安心して長く続けられている" },
  { value: SuccessEnum.TaxAchieved, label: "節税ができている" },
  { value: SuccessEnum.DebtFreeAsset, label: "ローンを返し終えた後も、価値のある資産が残っている" },
  { value: SuccessEnum.AbleToExpand, label: "2件目、3件目へ無理なく増やせる状態になっている" },
];

// Q5 毎月の自己負担（単一回答）
export const MONTHLY_OPTIONS: Option<MonthlyEnum>[] = [
  { value: MonthlyEnum.NearZero, label: "毎月の自己負担は避けたい" },
  { value: MonthlyEnum.Within5k, label: "月5,000円程度まで" },
  { value: MonthlyEnum.Within10k, label: "月1万円程度まで" },
  { value: MonthlyEnum.Within30k, label: "月3万円程度まで" },
  { value: MonthlyEnum.Within50k, label: "月5万円程度まで" },
  { value: MonthlyEnum.Over50k, label: "月5万円を超えても、将来性があれば検討できる" },
];

// Q6 借入について（単一回答・毎月の自己負担と同じ画面）
export const BORROWING_OPTIONS: Option<BorrowingStanceEnum>[] = [
  { value: BorrowingStanceEnum.StartSmallFirstLoan, label: "ローンを組んだことがなく、できるだけ小さく始めたい" },
  { value: BorrowingStanceEnum.OkIfRentCovers, label: "家賃で返せる範囲なら、借りること自体は気にならない" },
  { value: BorrowingStanceEnum.AnxiousWithMortgage, label: "住宅ローンがあるので、さらに借りるのは不安" },
  { value: BorrowingStanceEnum.StartBigIfPossible, label: "借りられるなら、多少大きくても始めたい" },
  { value: BorrowingStanceEnum.UnsureEligibility, label: "そもそも借りられるかどうかが分からない" },
];

// Q7 保有・売却についての考え方（単一回答）
export const HOLDING_OPTIONS: Option<HoldingEnum>[] = [
  { value: HoldingEnum.Within5y, label: "条件がよければ、5年以内の売却も検討したい" },
  { value: HoldingEnum.About10y, label: "10年程度保有し、状況を見て売却したい" },
  { value: HoldingEnum.About20y, label: "20年以上、家賃収入を得ながら保有したい" },
  { value: HoldingEnum.About30y, label: "ローン完済まで、長期で保有したい" },
  { value: HoldingEnum.AfterPayoff, label: "完済後も、収入を生む資産として持ち続けたい" },
];

// Q8 安全性と収益（1〜5スケール・単一）
export const LOCATION_YIELD = {
  min: 1,
  max: 5,
  leftLabel: "立地を\n重視",
  rightLabel: "利回りを\n重視",
  notes: [
    {
      label: "立地",
      text: "利回りは低めでも、駅から近く、空室が出にくい部屋を選びたい方",
      example: "例）都心・駅徒歩5分。家賃は控えめだが、入居者が途切れにくい",
    },
    {
      label: "利回り",
      text: "エリアを広げてでも、利回りが高い部屋を選びたい方",
      example: "例）郊外・駅徒歩15分。価格に対して家賃が高く収益は多いが、空室が出ることがある",
    },
  ],
  footnote: "※ 利回り＝1年間の家賃収入÷物件価格。高いほど収益は大きいが、空室のリスクも上がりやすい",
  // 回答一覧では改行を含めずに表示する。
  answerLabels: { left: "立地を重視", right: "利回りを重視" },
};

// Q9 購入後の運用への関わり方（単一回答）
export const MANAGEMENT_OPTIONS: Option<ManagementEnum>[] = [
  { value: ManagementEnum.FullyDelegated, label: "できるだけすべて任せたい" },
  { value: ManagementEnum.DelegatedWithReport, label: "定期的に状況を確認しながら進めたい" },
  { value: ManagementEnum.KeyDecisionsOnly, label: "基本は任せて、重要なときだけ判断したい" },
  { value: ManagementEnum.SelfDirected, label: "自分でも情報を調べ、改善を考えたい" },
  { value: ManagementEnum.ActiveLandlord, label: "大家業の一つとして、積極的に関わりたい" },
];

// Q10 売却のタイミング（単一回答）
export const SALE_TIMING_OPTIONS: Option<SaleIntentEnum>[] = [
  { value: SaleIntentEnum.Yes, label: "利益が出るなら、早めに売却して確定したい" },
  { value: SaleIntentEnum.Conditional, label: "家賃収入と売却利益を比べて、有利な方を選びたい" },
  { value: SaleIntentEnum.No, label: "多少価格が上がっても、長く持ち続けたい" },
  { value: SaleIntentEnum.Neutral, label: "専門家の意見を聞きながら判断したい" },
];

// Q12 家賃収入の使いみち（単一回答・「これからの暮らし」画面）
export const RENTAL_INCOME_USE_OPTIONS: Option<RentalIncomeUseEnum>[] = [
  { value: RentalIncomeUseEnum.Savings, label: "貯金に回す" },
  { value: RentalIncomeUseEnum.LivingExpenses, label: "生活費に足す" },
  { value: RentalIncomeUseEnum.LoanPrepayment, label: "ローンの繰り上げ返済" },
  { value: RentalIncomeUseEnum.Family, label: "家族のために使う" },
  { value: RentalIncomeUseEnum.NextProperty, label: "次の物件やその他金融資産投資に回す" },
];

// Q13 働き方の見通し（単一回答・「これからの暮らし」画面）
export const WORK_OUTLOOK_OPTIONS: Option<WorkOutlookEnum>[] = [
  { value: WorkOutlookEnum.Until60, label: "60歳まで" },
  { value: WorkOutlookEnum.Until65, label: "65歳まで" },
  { value: WorkOutlookEnum.Until70, label: "70歳まで" },
  { value: WorkOutlookEnum.AsLongAsAble, label: "体が動くうちは" },
  { value: WorkOutlookEnum.Undecided, label: "決めていない" },
];

// Q14 1件目の時期（単一回答・「進め方の希望」画面）
export const FIRST_PURCHASE_TIMING_OPTIONS: Option<FirstPurchaseTimingEnum>[] = [
  { value: FirstPurchaseTimingEnum.Within1m, label: "1ヶ月以内" },
  { value: FirstPurchaseTimingEnum.Within3m, label: "3ヶ月以内" },
  { value: FirstPurchaseTimingEnum.Within6m, label: "6ヶ月以内" },
  { value: FirstPurchaseTimingEnum.Within1y, label: "1年以内" },
  { value: FirstPurchaseTimingEnum.ConsultPlanner, label: "プランナーに相談して考える" },
];
// 注記は必ず選択肢の下に置く（上に置くと、選ぶ前に基準を見せて誘導が強くなりすぎるため）
// 販促文言として継続掲載（2026-10-01 依頼者確認）。集計処理による自動更新値ではない。
// 文言・数値の改訂時はこの定数を更新し、入力画面と結果画面へ同時に反映する。
export const FIRST_PURCHASE_TIMING_NOTE = "プロパリーでは、1件目を決めた方の中央値は登録から2.4ヶ月です。";

// Q15 比べたいプランナーの人数（単一回答・「進め方の希望」画面）
export const PLANNER_COMPARISON_OPTIONS: Option<PlannerComparisonCountEnum>[] = [
  { value: PlannerComparisonCountEnum.One, label: "1人でいい" },
  { value: PlannerComparisonCountEnum.TwoToThree, label: "2〜3人比べたい" },
  { value: PlannerComparisonCountEnum.FiveOrMore, label: "5人以上比べたい" },
  { value: PlannerComparisonCountEnum.TenOrMore, label: "10人以上比べたい" },
  { value: PlannerComparisonCountEnum.ConsultPlanner, label: "プランナーに相談して考える" },
];
export const PLANNER_COMPARISON_NOTE = "プロパリーでは、多くの方が複数人を見比べてから決めています。";

// Q16 プランナーに相談したいこと（複数・最低1・最大5）
export const MEETING_ITEM_MAX = 5;
export const MEETING_ITEM_OPTIONS: Option<MeetingItemEnum>[] = [
  {
    value: MeetingItemEnum.MonthlyRealCost,
    label: "毎月、実際にいくら収入が入り、いくら自己負担が発生するのか知りたい",
  },
  { value: MeetingItemEnum.VacancyCost, label: "空室になった場合、どのくらい自己負担が増えるのか知りたい" },
  { value: MeetingItemEnum.RentDecline, label: "家賃が下がった場合でも、収支が成り立つのか知りたい" },
  { value: MeetingItemEnum.InterestRateRise, label: "金利が上がった場合でも、無理なく返済を続けられるのか知りたい" },
  { value: MeetingItemEnum.RepairCost, label: "修繕や設備交換に、いつ・どのくらいの費用がかかるのか知りたい" },
  { value: MeetingItemEnum.TaxEffect, label: "自分の場合、どのくらい節税効果が見込めるのか知りたい" },
  { value: MeetingItemEnum.SalePrice, label: "将来、どのくらいの価格で売却できそうか知りたい" },
  { value: MeetingItemEnum.Comparison, label: "ほかの会社や物件と比べて、どのような違いがあるのか知りたい" },
  { value: MeetingItemEnum.UnfitCase, label: "自分には向いていない物件や、避けた方がよい条件を知りたい" },
  { value: MeetingItemEnum.LoanConsult, label: "ローンや融資についても相談したい" },
  { value: MeetingItemEnum.SupportDetail, label: "購入後の管理やサポート内容を確認しておきたい" },
];

// Q17 希望する説明の仕方（複数選択可）
export const EXPLANATION_OPTIONS: Option<ExplanationEnum>[] = [
  { value: ExplanationEnum.BeginnerFriendly, label: "一つひとつ順番に、基礎から丁寧に説明してほしい" },
  { value: ExplanationEnum.WithNumbers, label: "具体的な数字や収支シミュレーションを見せてほしい" },
  { value: ExplanationEnum.RiskFirst, label: "メリットだけでなく、リスクや失敗例も率直に伝えてほしい" },
  { value: ExplanationEnum.ProsCons, label: "複数の選択肢を比較しながら説明してほしい" },
  { value: ExplanationEnum.VsOtherInvestments, label: "他の投資との違いについても説明してほしい" },
  { value: ExplanationEnum.NoJargon, label: "専門用語を使わず、初心者にも分かる言葉で説明してほしい" },
  { value: ExplanationEnum.WithRationale, label: "結論だけでなく、その根拠まで詳しく説明してほしい" },
  { value: ExplanationEnum.ConcisePoints, label: "細かい説明より、重要なポイントを簡潔に伝えてほしい" },
  { value: ExplanationEnum.OwnPace, label: "質問しながら、自分のペースで理解を深めたい" },
  { value: ExplanationEnum.ProactiveSuggestion, label: "自分に合う選択肢を、積極的に提案してほしい" },
];

// Q18 プランナーに伝えておきたいこと（自由記述・任意）
export const MEETING_FREE_TEXT_PLACEHOLDER = "例文：\n無理に購入を勧めず、メリットとリスクの両方を率直に教えてほしい";

// Q11 今感じている不安（各項目3段階・売却のタイミングの次）
export const ANXIETY_ROWS: { key: AnxietyKey; label: string }[] = [
  { key: "anxiety_loan", label: "ローンを無理なく返済し続けられるか" },
  { key: "anxiety_vacancy", label: "入居者が見つからず、家賃収入がなくなること" },
  { key: "anxiety_exit", label: "将来、希望する価格やタイミングで売却できないこと" },
  { key: "anxiety_repair", label: "予想していなかった修繕費や管理費がかかること" },
  { key: "anxiety_sales_pressure", label: "十分に理解・納得しないまま、購入を勧められること" },
];
export const ANXIETY_LEVELS: Option<AnxietyLevelEnum>[] = [
  { value: AnxietyLevelEnum.Strong, label: "とても不安" },
  { value: AnxietyLevelEnum.Normal, label: "少し不安" },
  { value: AnxietyLevelEnum.Weak, label: "不安はない" },
];

// Q19 避けたいこと（複数・最低1件必須）
export const AVOID_OPTIONS: Option<AvoidEnum>[] = [
  { value: AvoidEnum.Overpriced, label: "相場よりも高い価格で物件を購入すること" },
  { value: AvoidEnum.PushyImmediate, label: "十分に比較せず、その場で決断を求められること" },
  { value: AvoidEnum.ExcessiveLoan, label: "自分の収入に対して、借入額が大きくなりすぎること" },
  { value: AvoidEnum.TaxOnly, label: "節税効果だけを理由に、購入を勧められること" },
  { value: AvoidEnum.HighManagement, label: "購入後の管理や対応に、大きな手間がかかること" },
];

// 入力画面と回答一覧で同じ質問文を表示する。キーは既存の識別番号（Q2は廃止）。
// Q5/Q6、Q12/Q13、Q14/Q15、Q17/Q18 は入力では同じ画面。
// 今感じている不安（Q11）は売却のタイミング（Q10）の次。追加画面（Q12〜Q15）はその後ろ。
export const DIAGNOSIS_QUESTION_TEXT: Record<number, string> = {
  1: "不動産投資で、どんな未来を実現したいですか？（複数選択可）",
  3: "物件や投資方法を選ぶとき、特に大切にしたいことは何ですか？（高い順に3つ選択）",
  4: "将来、どのような状態になれば「不動産投資を始めてよかった」と思えそうですか？（複数選択可）",
  5: "不動産投資のために、毎月の家計から無理なく負担できる金額はどれくらいですか？（単一回答）",
  6: "借入について、どう感じますか？（単一回答）",
  7: "購入した不動産を、将来どのように持ち続けたいですか？（単一回答）",
  8: "物件選びで、どちらをより重視したいですか？（単一回答）",
  9: "物件を購入した後、運用にはどのくらい自分で関わりたいですか？（単一回答）",
  10: "購入した物件の価格が上がり、利益が見込める場合、どのように考えたいですか？（単一回答）",
  11: "不動産投資を考えるうえで、次のことにどのくらい不安を感じますか？",
  12: "家賃収入が入るようになったら、まず何に使いたいですか？（単一回答）",
  13: "今の働き方は、何歳ごろまで続ける予定ですか？（単一回答）",
  14: "1件目は、いつごろ見つけたいですか？（単一回答）",
  15: "相談するなら、何人くらい見てみたいですか？（単一回答）",
  16: "不動産投資のプロに相談する際に、特にどんなことを確認しておきたいですか？（複数選択可）",
  17: "プランナーから、どのように説明してもらえると理解しやすいですか？（複数選択可）",
  18: "プランナーへ相談する際に、上記以外で事前に伝えておきたいことはありますか？（自由記述）",
  19: "不動産投資を進めるうえで、「これは避けたい」と思うことを教えてください（複数回答可）",
};
